import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'recompose';
import includes from 'lodash/includes';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import size from 'lodash/size';
import CSSModules from 'react-css-modules';
import moment from 'moment';
import classNames from 'classnames';

import TextInput from '../../TextInput';
import SelectCustom from '../../SelectCustom';
import DefinitionList from '../../DefinitionList';
import ImageUploader from '../../common/ImageUploader';
import PasswordInput from '../../TextInput/PasswordInput';
import DatePickerCustom from '../../common/DatePickerCustom';
import { FIELDS_TYPE } from '../../../constants';
import handleDOB, { handleAge } from '../../../utils/handleDOB';
import { getPhoneWithoutUSCode } from '../../../utils/getPhone';
import { PROFILE_FIELDSET_EMPTY } from '../../../constants/strings';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { TimePickerRange } from '../../common/TimePickerRange';
import { formatPhone } from '../../../utils/converters/phoneFormatter';
import { formatTimeRangeView } from '../../common/TimePickerRange/utils';
import { MobilePhone } from '../../MobilePhone';
import styles from '../index.styl';
import MedicationSelect from '../MedicationSelect';
import PreferredPharmacy from '../PreferredPharmacy';
import { makePharmacyTitle } from '../../../utils/makePharmacyTitle';

const LOCAL_FIELDS_TYPES = {
  ...FIELDS_TYPE,
  MEDICATION_SELECT: 'medication',
  PHARMACY: 'pharmacy',
};

class Field extends PureComponent {
  state = {
    dirty: false,
  };

  getValueFromItem = item => {
    return item && isPlainObject(item) ? item.value : item;
  };

  get dobValue() {
    const {
      children: { value },
    } = this.props;

    return moment(value, 'MM-DD-YYYY').toDate();
  }

  notLastItemInComplexValue = (values, currentIndex) => {
    return values
      .slice(currentIndex + 1)
      .map(this.getValueFromItem)
      .some(i => i);
  };

  // helper for displaying correct text in view mode
  getValue = (value, type, fieldName, separator = ', ', src) => {
    const {
      children,
      intl: { formatMessage },
      defaultEmptyValue,
      editMode,
    } = this.props;
    const { options, disabledText, transformValueFromOptions } = children;
    const shownValue = [];
    const emptyState = defaultEmptyValue || (
      <FormattedMessage {...PROFILE_FIELDSET_EMPTY} />
    );

    if (type !== FIELDS_TYPE.IMAGE && !value) return emptyState;

    if (type === FIELDS_TYPE.CUSTOM_TIME_PICKER)
      return formatTimeRangeView(value);

    if (type === LOCAL_FIELDS_TYPES.PHARMACY) {
      if (typeof value === 'string') return value;
      return makePharmacyTitle(value, true);
    }

    if (isArray(value)) {
      if (!size(value)) return emptyState;

      value.forEach((item, i, array) => {
        let currentValue = this.getValueFromItem(item);

        if (transformValueFromOptions && options) {
          const matchedOption = options.find(opt => opt.value === currentValue);

          currentValue = matchedOption ? matchedOption.label : currentValue;
        }

        if (currentValue) {
          shownValue.push(currentValue);

          if (this.notLastItemInComplexValue(array, i)) {
            shownValue.push(separator || item.separator);
          }
        }
      });
    } else if (fieldName === PROFILE_FIELDS_TYPES.DOB) {
      return handleDOB(value, formatMessage);
    } else if (fieldName === PROFILE_FIELDS_TYPES.AGE) {
      return handleAge(value, formatMessage);
    } else if (type === FIELDS_TYPE.SELECT && size(options)) {
      // show label instead of value
      const rawVal = options.find(item => item.value === value);
      return rawVal && rawVal.label ? rawVal.label : value;
    } else if (fieldName === PROFILE_FIELDS_TYPES.EMAIL) {
      return value || emptyState;
    } else if (fieldName === PROFILE_FIELDS_TYPES.PHONE_NUMBER) {
      return value ? getPhoneWithoutUSCode(formatPhone(value)) : emptyState;
    } else if (type === FIELDS_TYPE.IMAGE) {
      return (
        <ImageUploader
          disabled={!editMode}
          label={disabledText}
          src={src}
          name={fieldName}
        />
      );
    } else {
      return value;
    }

    return shownValue.join('');
  };

  handleBlur = (...params) => {
    const { onBlur } = this.props;

    if (onBlur) onBlur(...params);
    this.setState({ dirty: true });
  };

  render() {
    const { dirty } = this.state;
    const {
      children,
      editMode,
      onChange,
      fieldStyle,
      className,
      defaultEmptyValue,
      intl: { formatMessage },
    } = this.props;
    const {
      name,
      src,
      value,
      disabled,
      separated,
      type,
      multi,
      options,
      fullWidth,
      hintText,
      fieldName,
      separator,
      text,
      limit,
      onCheckBeforeDelete,
      id,
      placeholder,
      shouldClearWhenOnFocus,
      transformValue,
      hideEllipsis,
      withManualInputDob,
    } = children;
    const isImageField = type === FIELDS_TYPE.IMAGE;
    const fieldValue = isImageField ? src : value;

    return (
      <div
        styleName={classNames(fullWidth ? 'fieldWrapperFull' : 'fieldWrapper', {
          'fieldWrapper--separated': editMode && separated,
          mt0: className === 'pharmacy-phone',
        })}
        className={`user-profile-field-${className}`}
      >
        <Choose>
          <When condition={!editMode}>
            <If condition={Boolean(defaultEmptyValue) || Boolean(fieldValue)}>
              <DefinitionList
                styleName="field"
                definition={name}
                hintText={hintText}
                hideEllipsis={hideEllipsis}
                description={this.getValue(
                  value,
                  type,
                  fieldName,
                  separator,
                  src,
                )}
                multiline
                hasOpacity={!isImageField}
                descriptionRenderer={transformValue}
              />
            </If>
          </When>
          <Otherwise>
            <div
              styleName={classNames('field', { 'field--separated': separated })}
              style={fieldStyle}
            >
              <Choose>
                <When condition={type === LOCAL_FIELDS_TYPES.SELECT}>
                  <SelectCustom
                    multi={multi}
                    options={options}
                    placeholder={name}
                    label={name}
                    value={value}
                    onCheckBeforeDelete={onCheckBeforeDelete}
                    onChange={val => onChange(val, dirty)}
                    onBlur={this.handleBlur}
                    disabled={disabled}
                  />
                </When>
                <When condition={type === LOCAL_FIELDS_TYPES.MEDICATION_SELECT}>
                  <MedicationSelect
                    multi={multi}
                    placeholder={name}
                    label={name}
                    value={value}
                    onCheckBeforeDelete={onCheckBeforeDelete}
                    onChange={val => onChange(val, dirty)}
                    onBlur={this.handleBlur}
                  />
                </When>
                <When condition={type === LOCAL_FIELDS_TYPES.DATE}>
                  <DatePickerCustom
                    label={name}
                    value={this.dobValue}
                    onChange={onChange}
                    maxDate={new Date()}
                    onBlur={this.handleBlur}
                    disabled={disabled}
                    withManualInputDob={withManualInputDob}
                  />
                </When>
                <When condition={type === LOCAL_FIELDS_TYPES.IMAGE}>
                  <ImageUploader
                    label={text}
                    title={name}
                    src={src}
                    name={fieldName}
                    onChange={val => onChange(val)}
                    id={id}
                    maxFileSize={limit}
                  />
                </When>
                <When condition={type === LOCAL_FIELDS_TYPES.PASSWORD}>
                  <PasswordInput
                    disabled={disabled}
                    autoresize
                    value={value}
                    placeholder={placeholder || name}
                    label={name}
                    onChange={(num, val) => onChange(val, dirty)}
                    onBlur={this.handleBlur}
                    shouldClearWhenOnFocus={shouldClearWhenOnFocus}
                    limit={limit}
                  />
                </When>
                <When condition={type === LOCAL_FIELDS_TYPES.MOBILE_PHONE}>
                  <MobilePhone
                    disabled={disabled}
                    value={value}
                    placeholder={name}
                    dirty={dirty}
                    label={name}
                    formatMessage={formatMessage}
                    onBlur={this.handleBlur}
                    onChange={onChange}
                  />
                </When>
                <When
                  condition={type === LOCAL_FIELDS_TYPES.CUSTOM_TIME_PICKER}
                >
                  <TimePickerRange
                    value={value || ''}
                    name={name}
                    onChange={val => onChange(val, dirty)}
                    label={name}
                    formatMessage={formatMessage}
                  />
                </When>
                <When condition={type === LOCAL_FIELDS_TYPES.PHARMACY}>
                  <PreferredPharmacy
                    value={value}
                    placeholder={name}
                    label={name}
                    onChange={val => onChange(val, dirty)}
                    onHandleBlur={this.handleBlur}
                  />
                </When>
                <Otherwise>
                  <TextInput
                    disabled={disabled}
                    autoresize
                    value={value}
                    placeholder={name}
                    label={name}
                    type={
                      includes(['date', 'email', 'number'], type)
                        ? type
                        : 'text'
                    }
                    textarea={type === LOCAL_FIELDS_TYPES.TEXTAREA}
                    onChange={val => onChange(val, dirty)}
                    onBlur={this.handleBlur}
                    limit={limit}
                  />
                </Otherwise>
              </Choose>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

Field.propTypes = {
  children: PropTypes.object,
  editMode: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  fieldStyle: PropTypes.object,
  className: PropTypes.string,
  defaultEmptyValue: PropTypes.string,
};

export default compose(
  injectIntl,
  CSSModules(styles, { allowMultiple: true }),
)(Field);



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/Field/index.jsx